import { useEffect, useRef, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EnvironmentVmPanel } from './EnvironmentVmPanel';

export type EnvironmentStatus = 'pending' | 'provisioning' | 'ready' | 'failed';

export interface EnvironmentStatusResponse {
  status: EnvironmentStatus;
  reason?: string;
  message?: string;
  connection?: {
    dnsRecord?: string;
    dnsZone?: string;
  };
  /** Ready environments: labelled URLs to open (BC server, Portal API/web server, ...). */
  urls?: { label: string; url: string }[];
  /** RFC3339 timestamp this environment is due to expire - see environment-status-backend's
   * resolveEnvironmentStatus, which surfaces the claim's status.expiresAt (Phase 4). */
  expiresAt?: string;
  /** The claim is gone from git and only waiting for Argo CD to remove it from the cluster. */
  deleting?: boolean;
}

interface AdminLogin {
  username?: string;
  password: string;
}

interface ExtendResponse {
  commitId: string;
  newExpiresAt: string;
}

/** Statuses for which extending is meaningful - a pending environment has no claim.yaml to
 * patch yet, and a failed one is Phase 4 Task 2/3's territory (lifecycle cleanup), not this
 * button's. */
const EXTENDABLE_STATUSES: ReadonlySet<EnvironmentStatus> = new Set(['provisioning', 'ready']);

/** How often the card re-checks while the environment is still coming up. Without this the card
 * showed whatever the first request returned (typically Provisioning) until the page was
 * reloaded. */
export const STATUS_POLL_INTERVAL_MS = 10_000;

/** An environment may not expire more than this far from now (mirrors the backend's cap). */
const MAX_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

const STATUS_LABEL: Record<EnvironmentStatus, string> = {
  pending: 'Pending',
  provisioning: 'Provisioning',
  ready: 'Ready',
  failed: 'Failed',
};

const STATUS_COLOR: Record<EnvironmentStatus, string> = {
  pending: '#9e9e9e',
  provisioning: '#1976d2',
  ready: '#2e7d32',
  failed: '#c62828',
};

async function fetchEnvironmentStatus(
  discoveryApi: { getBaseUrl(pluginId: string): Promise<string> },
  fetchApi: { fetch: typeof fetch },
  name: string,
): Promise<EnvironmentStatusResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('environment-status');
  const response = await fetchApi.fetch(`${baseUrl}/${encodeURIComponent(name)}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch environment status for ${name}: ${response.status}`);
  }
  return response.json();
}

async function extendEnvironment(
  discoveryApi: { getBaseUrl(pluginId: string): Promise<string> },
  fetchApi: { fetch: typeof fetch },
  name: string,
): Promise<ExtendResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('environment-status');
  const response = await fetchApi.fetch(`${baseUrl}/${encodeURIComponent(name)}/extend`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to extend environment ${name}: ${response.status}`);
  }
  return response.json();
}

async function deleteEnvironment(
  discoveryApi: { getBaseUrl(pluginId: string): Promise<string> },
  fetchApi: { fetch: typeof fetch },
  name: string,
): Promise<void> {
  const baseUrl = await discoveryApi.getBaseUrl('environment-status');
  const response = await fetchApi.fetch(`${baseUrl}/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete environment ${name}: ${response.status}`);
  }
}

/** Fetches the VM admin login. The backend only answers the person who requested the environment;
 * for anyone else it says so, and that message is shown as is. */
async function fetchAdminLogin(
  discoveryApi: { getBaseUrl(pluginId: string): Promise<string> },
  fetchApi: { fetch: typeof fetch },
  name: string,
): Promise<AdminLogin> {
  const baseUrl = await discoveryApi.getBaseUrl('environment-status');
  const response = await fetchApi.fetch(`${baseUrl}/${encodeURIComponent(name)}/admin-password`, {
    method: 'POST',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new Error(body?.message ?? `Failed to get the admin login: ${response.status}`);
  }
  return response.json();
}

/**
 * Entity-page card showing a NavOneQCoreEnvironment's live status (Phase 3, Task 4). Reflects
 * exactly what the backend reports - never assumes `ready` just because the underlying claim
 * looked healthy; see environment-status-backend for the check this depends on (claim
 * Ready=True AND the real Azure VirtualMachineExtension provisioningState is Succeeded).
 */
export function EnvironmentStatusCard() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [result, setResult] = useState<EnvironmentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [adminLogin, setAdminLogin] = useState<AdminLogin | null>(null);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const settled = useRef(false);
  settled.current =
    (result?.status === 'ready' || result?.status === 'failed') && !result?.deleting;

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchEnvironmentStatus(discoveryApi, fetchApi, entity.metadata.name)
        .then(response => {
          if (!cancelled) {
            setResult(response);
            setError(null);
          }
        })
        .catch(err => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : String(err));
          }
        });
    load();
    // Keep polling until the environment reaches a terminal state.
    const timer = setInterval(() => {
      if (!settled.current) {
        load();
      }
    }, STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [discoveryApi, fetchApi, entity.metadata.name]);

  if (error) {
    return (
      <div>
        <h3>Environment status</h3>
        <p>Failed to load environment status: {error}</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div>
        <h3>Environment status</h3>
        <p>Loading...</p>
      </div>
    );
  }

  const isDeleting = Boolean(result.deleting) || deleteRequested;
  const isExpired = Boolean(result.expiresAt && new Date(result.expiresAt).getTime() <= Date.now());
  // Only actionable while the environment is still provisioning/ready AND not already past its
  // expiry - an environment without a known expiresAt yet (Composition hasn't resolved one) has
  // nothing to extend either.
  const atMaxLifetime = Boolean(
    result.expiresAt && new Date(result.expiresAt).getTime() >= Date.now() + MAX_LIFETIME_MS,
  );
  const canExtend =
    !isDeleting &&
    EXTENDABLE_STATUSES.has(result.status) &&
    Boolean(result.expiresAt) &&
    !isExpired &&
    !atMaxLifetime;

  async function handleExtend() {
    setExtendError(null);
    setExtending(true);
    try {
      const { newExpiresAt } = await extendEnvironment(
        discoveryApi,
        fetchApi,
        entity.metadata.name,
      );
      setResult(current => (current ? { ...current, expiresAt: newExpiresAt } : current));
    } catch (err) {
      setExtendError(err instanceof Error ? err.message : String(err));
    } finally {
      setExtending(false);
    }
  }

  async function handleShowLogin() {
    setLoginError(null);
    setLoadingLogin(true);
    try {
      setAdminLogin(await fetchAdminLogin(discoveryApi, fetchApi, entity.metadata.name));
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingLogin(false);
    }
  }

  // Any state with a claim in git can be deleted, including failed and expired ones; only
  // `pending` (no claim yet) has nothing to remove.
  const canDelete = result.status !== 'pending' && !isDeleting;

  async function handleDelete() {
    const name = entity.metadata.name;
    if (!window.confirm(`Delete environment ${name}? Its resources will be torn down and this cannot be undone.`)) {
      return;
    }
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteEnvironment(discoveryApi, fetchApi, name);
      setDeleteRequested(true);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <h3>Environment status</h3>
      <div
        data-testid="environment-status-indicator"
        style={{ color: STATUS_COLOR[result.status], fontWeight: 'bold' }}
      >
        {STATUS_LABEL[result.status]}
      </div>
      {result.status === 'failed' && (result.message || result.reason) && (
        <p data-testid="environment-status-failure-reason">
          {result.reason ? `${result.reason}: ` : ''}
          {result.message}
        </p>
      )}
      {result.status === 'ready' && result.urls && result.urls.length > 0 && (
        <ul data-testid="environment-status-connection" style={{ paddingLeft: 20 }}>
          {result.urls.map(({ label, url }) => (
            <li key={label}>
              {label}:{' '}
              <a href={url} target="_blank" rel="noopener noreferrer" data-testid="environment-status-url">
                {url}
              </a>
            </li>
          ))}
        </ul>
      )}
      {result.status === 'ready' && !isDeleting && (
        <div data-testid="environment-status-admin-login">
          {adminLogin ? (
            <>
              <p>
                VM admin login:{' '}
                <strong data-testid="environment-status-admin-username">{adminLogin.username}</strong> /{' '}
                <code data-testid="environment-status-admin-password">{adminLogin.password}</code>
              </p>
              <button type="button" onClick={() => setAdminLogin(null)}>
                Hide
              </button>
            </>
          ) : (
            <button type="button" onClick={handleShowLogin} disabled={loadingLogin}>
              {loadingLogin ? 'Loading...' : 'Show admin login'}
            </button>
          )}
          {loginError && <p>{loginError}</p>}
        </div>
      )}
      {result.status === 'ready' && !isDeleting && (
        <EnvironmentVmPanel environmentName={entity.metadata.name} />
      )}
      {result.expiresAt && (
        <p data-testid="environment-status-expiry">
          {isExpired ? 'Expired ' : 'Expires '}
          {new Date(result.expiresAt).toLocaleDateString()}
        </p>
      )}
      {canExtend && (
        <button
          type="button"
          onClick={handleExtend}
          disabled={extending}
          style={{ marginRight: 8 }}
        >
          {extending ? 'Extending...' : 'Extend'}
        </button>
      )}
      {extendError && <p>Failed to extend: {extendError}</p>}
      {canDelete && (
        <button type="button" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      )}
      {isDeleting && (
        <p data-testid="environment-status-delete-requested">
          Deletion requested. The environment will be torn down shortly.
        </p>
      )}
      {deleteError && <p>Failed to delete: {deleteError}</p>}
    </div>
  );
}
